import { Link } from 'react-router-dom';
import { useFoundingSeats } from '../hooks/useFoundingSeats';
import './FoundingOffer.css';

/** Founding-cohort section id — kept in sync with the `sections` entry so the
 * scroll ticker, IntersectionObserver and video parallax all track it. */
export const FOUNDING_OFFER_ID = 'founding-offer';

const ASSURANCES = ['No credit card required', 'Cancel anytime', 'Core AI · Ask Revvly included'];

function CheckIcon() {
  return (
    <svg
      className="founding__check"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

interface FoundingOfferProps {
  /** Drives the entrance reveal — set when this is the active scroll section. */
  isActive: boolean;
}

/**
 * Closing founding-offer CTA on the marketing home page. Sits between the last
 * scroll section and the footer and shows the live remaining-seat count.
 */
export function FoundingOffer({ isActive }: FoundingOfferProps) {
  const { seatsRemaining, seatsTotal, soldOut, status, hasLiveCount } = useFoundingSeats();
  const remaining = seatsRemaining ?? seatsTotal;

  // Don't assert a specific number until a live count loads.
  const seatLabel = !hasLiveCount
    ? 'limited seats'
    : soldOut
      ? 'cohort full'
      : `${remaining} ${remaining === 1 ? 'seat' : 'seats'} left`;

  const claimLabel = status === 'unavailable' ? 'Get early access' : soldOut ? 'Join the waitlist' : 'Claim your seat';

  return (
    <section id={FOUNDING_OFFER_ID} className="section founding" aria-label="Founding offer">
      <div className={`founding__card ${isActive ? 'founding__card--visible' : ''}`}>
        <p className="founding__eyebrow">Founding offer · {seatLabel}</p>

        <h2 className="founding__headline">
          Get <em className="founding__accent">6 months free</em>
          <span className="founding__accent">.</span>
        </h2>

        <p className="founding__lead">
          The first {seatsTotal} teams to sign up get six full months of Revvly at no cost. In
          return, we ask for one 15-minute call a month with our CEO, your feedback shapes the
          roadmap.
        </p>

        <p className="founding__note">And every new module we ship lands in your account, free.</p>

        <div className="founding__actions">
          <Link to="/early-access" className="founding__btn founding__btn--accent">
            {claimLabel} <span aria-hidden>›</span>
          </Link>
          <Link to="/early-access" className="founding__btn founding__btn--light">
            Talk to the founders
          </Link>
        </div>

        <ul className="founding__assurances">
          {ASSURANCES.map((item) => (
            <li key={item} className="founding__assurance">
              <CheckIcon />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
